import React, { useEffect, useState } from "react";
import Table from "@components/table";
import StoreEmployee from "@apis/storeEmployee";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import Column from "@components/column";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

const StoreSubAdmins = () => {
  const navigate = useNavigate();
  const employeesState = useSelector(
    (state: RootState) => state.storeEmployees,
  );

  const employees = Array.isArray(employeesState?.data)
    ? employeesState.data
    : Array.isArray(employeesState?.items)
      ? employeesState.items
      : [];

  const [page, setPage] = useState(1);

  const fetchEmployees = async () => {
    try {
      await StoreEmployee.list();
    } catch (error) {
      console.error("Failed to fetch store employees:", error);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [page]);

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to remove this team member?")) {
      try {
        await StoreEmployee.remove(id);
        toast.success("Team member removed successfully");
        await fetchEmployees();
      } catch (error) {
        console.error("Failed to remove team member:", error);
        toast.error("Failed to remove team member");
      }
    }
  };

  const columns = [
    {
      title: "ID",
      key: "id",
      render: (item: any) => (
        <span style={{ fontWeight: 600, color: "#4a5568" }}>#{item?.id}</span>
      ),
    },
    {
      title: "Name",
      key: "name",
      render: (item: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {item?.avatar ? (
            <img
              src={item.avatar}
              alt={item.name}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #f1f5f9",
              }}
            />
          ) : (
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "#ffeAeb",
                color: "#f23236",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "14px",
              }}
            >
              {item?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{ fontWeight: 600, color: "#1e293b", fontSize: "14px" }}
            >
              {item?.name}
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "#f23236",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.02em",
              }}
            >
              {item?.roleName || "Team Member"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (item: any) => (
        <span style={{ color: "#718096" }}>{item?.email}</span>
      ),
    },
    {
      title: "Phone",
      key: "phoneNo",
      render: (item: any) => (
        <span style={{ color: "#718096" }}>{item?.phoneNo || "—"}</span>
      ),
    },
    {
      title: "Role",
      key: "roleName",
      render: (item: any) => (
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "8px",
            background: item?.roleName ? "#f0fdf4" : "#f8fafc",
            color: item?.roleName ? "#16a34a" : "#94a3b8",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          {item?.roleName || "No Role"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "isActive",
      render: (item: any) => (
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "8px",
            background: item?.isActive ? "#f0fdf4" : "#fef2f2",
            color: item?.isActive ? "#16a34a" : "#dc2626",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          {item?.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (item: any) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() =>
              navigate(`/store/users/sub-admins/edit/${item.id}`)
            }
            style={{
              padding: "6px",
              borderRadius: "6px",
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              color: "#64748b",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseOver={(e: any) => {
              e.currentTarget.style.color = "#f23236";
              e.currentTarget.style.borderColor = "#f23236";
            }}
            onMouseOut={(e: any) => {
              e.currentTarget.style.color = "#64748b";
              e.currentTarget.style.borderColor = "#e2e8f0";
            }}
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => handleDelete(item.id)}
            style={{
              padding: "6px",
              borderRadius: "6px",
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              color: "#64748b",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseOver={(e: any) => {
              e.currentTarget.style.background = "#fff1f2";
              e.currentTarget.style.color = "#e11d48";
              e.currentTarget.style.borderColor = "#e11d48";
            }}
            onMouseOut={(e: any) => {
              e.currentTarget.style.background = "#ffffff";
              e.currentTarget.style.color = "#64748b";
              e.currentTarget.style.borderColor = "#e2e8f0";
            }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Column>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#1a202c",
            margin: 0,
          }}
        >
          Sub Admins
        </h1>

        <button
          onClick={() => navigate("/store/users/sub-admins/create")}
          style={{
            padding: "10px 24px",
            borderRadius: "8px",
            background: "#f23236",
            color: "#ffffff",
            border: "none",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 4px 14px rgba(242, 50, 54, 0.39)",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
          onMouseOver={(e: any) =>
            (e.currentTarget.style.transform = "translateY(-1px)")
          }
          onMouseOut={(e: any) =>
            (e.currentTarget.style.transform = "translateY(0)")
          }
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Team
        </button>
      </div>

      <div
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          padding: "24px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
          border: "1px solid #f0f4f8",
        }}
      >
        <Table
          data={employees}
          columns={columns}
          totalItems={employees.length}
          totalPages={1}
          pageControlled={page}
          onPageChange={setPage}
          itemsPerPage={30}
          actions={[]}
        />
      </div>
    </Column>
  );
};

export default StoreSubAdmins;
